import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { QuillModule } from 'ngx-quill';
import { TranslateModule } from '@ngx-translate/core';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ArticlePreviewComponent } from './components/article-preview/article-preview.component';
import { BreadcrumbsComponent } from './components/breadcrumbs/breadcrumbs.component';
import { CharacterLimitComponent } from './components/character-limit/character-limit.component';
import { DatepickerComponent } from './components/datepicker/datepicker.component';
import { EmptyStateWrapperComponent } from './components/empty-state-wrapper/empty-state-wrapper.component';
import { GridCellComponent } from './components/grid/grid-cell/grid-cell.component';
import { GridDaterangeFilterComponent } from './components/grid/grid-daterange-filter/grid-daterange-filter.component';
import { GridHeaderCellComponent } from './components/grid/grid-header-cell/grid-header-cell.component';
import { GridOptionsFilterComponent } from './components/grid/grid-options-filter/grid-options-filter.component';
import { GridSortingComponent } from './components/grid/grid-sorting/grid-sorting.component';
import { GridTypeaheadFilterComponent } from './components/grid/grid-typeahead-filter/grid-typeahead-filter.component';
import { GridComponent } from './components/grid/grid.component';
import { ImageUploaderComponent } from './components/image-uploader/image-uploader.component';
import { TextInputComponent } from './components/input/text-input.component';
import { NotificationItemComponent } from './components/notification-item/notification-item.component';
import { OptionComponent } from './components/option/option.component';
import { RadioButtonComponent } from './components/radio-group/radio-button/radio-button.component';
import { RadioGroupComponent } from './components/radio-group/radio-group.component';
import { SearchFieldComponent } from './components/search-field/search-field.component';
import { SearchInputComponent } from './components/search-input/search-input.component';
import { SearchComponent } from './components/search/search.component';
import { SelectComponent } from './components/select/select.component';
import { TagsComponent } from './components/tags/tags.component';
import { TimepickerComponent } from './components/timepicker/timepicker.component';
import { TypeaheadTagsComponent } from './components/typeahead-tags/typeahead-tags.component';
import { UserAvaComponent } from './components/user-ava/user-ava.component';
import { UserTileComponent } from './components/user-tile/user-tile.component';
import { ValidationMessageComponent } from './components/validation-message/validation-message.component';
import { QuillEditorDirective } from './directives/quill-editor.directive';
import { DatepickerValueAcessorDirective } from './directives/datepicker.directive';
import { IndeterminateDirective } from './directives/indeterminate.directive';
import { TruncatedTooltipDirective } from './directives/truncated-tooltip.directive';
import { ValidationDirective } from './directives/validation.directive';
import { DateAgoPipe } from './pipes/date-ago.pipe';
import { DateFormatPipe } from './pipes/date-format.pipe';
import { DateTimeFormatPipe } from './pipes/date-time-format.pipe';
import { HighlightPipe } from './pipes/highlight.pipe';
import { ImagePipe } from './pipes/image.pipe';
import { SanitizeRichTextPipe } from './pipes/sanitize-rich-text.pipe';
import { CategoryCardComponent } from './components/category-card/category-card.component';
import { DaterangepickerComponent } from './components/daterangepicker/daterangepicker.component';
import { ChangeOrderComponent } from './components/change-order/change-order.component';
import { SearchWithOptionsComponent } from './components/search-with-options/search-with-options.component';
import { TreeSelectComponent } from './components/tree-select/tree-select.component';
import { TreeSelectItemComponent } from './components/tree-select-item/tree-select-item.component';
import { CustomListColumnMockDirective } from '@tests/mocks';
import { ListInputSelectionComponent } from '@shared/components/list-input-selection/list-input-selection.component';
import { MultiSelectComponent } from '@shared/components/multi-select/multi-select.component';
import { BuilderItemComponent } from '../admin/reports/csat-builder/components/builder-item/builder-item.component';
import { TextareaInputComponent } from '../admin/reports/csat-builder/components/textarea-input/textarea-input.component';
import { SurveyNegativeRadioComponent } from '../portal/feedback/common/survey-negative-modal/survey-negative-radio/survey-negative-radio.component';
import { SurveyNegativeCheckboxComponent } from '../portal/feedback/common/survey-negative-modal/survey-negative-checkbox/survey-negative-checkbox.component';
import { TableModule } from './components/table/table.module';
import { DateRangePickerComponent } from './components/date-range-picker/date-range-picker.component';

@NgModule({
  declarations: [
    UserAvaComponent,
    UserTileComponent,
    SearchComponent,
    GridComponent,
    GridHeaderCellComponent,
    GridCellComponent,
    GridSortingComponent,
    GridOptionsFilterComponent,
    GridTypeaheadFilterComponent,
    GridDaterangeFilterComponent,
    SearchFieldComponent,
    SearchInputComponent,
    DatepickerComponent,
    TimepickerComponent,
    NotificationItemComponent,
    DateAgoPipe,
    DateFormatPipe,
    DateTimeFormatPipe,
    HighlightPipe,
    IndeterminateDirective,
    QuillEditorDirective,
    DatepickerValueAcessorDirective,
    SelectComponent,
    MultiSelectComponent,
    OptionComponent,
    ValidationDirective,
    ValidationMessageComponent,
    ImagePipe,
    BreadcrumbsComponent,
    TypeaheadTagsComponent,
    TagsComponent,
    EmptyStateWrapperComponent,
    ImageUploaderComponent,
    RadioGroupComponent,
    TruncatedTooltipDirective,
    TextInputComponent,
    RadioButtonComponent,
    CharacterLimitComponent,
    ArticlePreviewComponent,
    CategoryCardComponent,
    DaterangepickerComponent,
    ChangeOrderComponent,
    SearchWithOptionsComponent,
    TreeSelectComponent,
    TreeSelectItemComponent,
    CustomListColumnMockDirective,
    ListInputSelectionComponent,
    TextareaInputComponent,
    BuilderItemComponent,
    SurveyNegativeRadioComponent,
    SurveyNegativeCheckboxComponent,
    DateRangePickerComponent
  ],
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    BsDropdownModule,
    BsDatepickerModule,
    PaginationModule,
    TypeaheadModule,
    QuillModule,
    FormsModule,
    TranslateModule,
    TooltipModule,
    TableModule,
    SanitizeRichTextPipe
  ],
  exports: [
    // components
    UserAvaComponent,
    UserTileComponent,
    SearchComponent,
    GridComponent,
    SearchInputComponent,
    SearchFieldComponent,
    DatepickerComponent,
    DaterangepickerComponent,
    TimepickerComponent,
    NotificationItemComponent,
    SelectComponent,
    MultiSelectComponent,
    OptionComponent,
    ValidationMessageComponent,
    BreadcrumbsComponent,
    TypeaheadTagsComponent,
    TagsComponent,
    ImageUploaderComponent,
    EmptyStateWrapperComponent,
    RadioGroupComponent,
    TextInputComponent,
    RadioButtonComponent,
    CharacterLimitComponent,
    ArticlePreviewComponent,
    CategoryCardComponent,
    ChangeOrderComponent,
    SearchWithOptionsComponent,
    TreeSelectComponent,
    ListInputSelectionComponent,
    TextareaInputComponent,
    BuilderItemComponent,
    SurveyNegativeRadioComponent,
    SurveyNegativeCheckboxComponent,
    // pipes
    DateAgoPipe,
    DateFormatPipe,
    DateTimeFormatPipe,
    HighlightPipe,
    ImagePipe,
    SanitizeRichTextPipe,

    // directives
    IndeterminateDirective,
    QuillEditorDirective,
    DatepickerValueAcessorDirective,
    ValidationDirective,
    TruncatedTooltipDirective,
    GridOptionsFilterComponent,

    DateRangePickerComponent
  ]
})
export class SharedModule {}

// Quill Editor usage
//
// declare in component:
// public quillModules = {
//   toolbar: [['bold'], ['link']]
// };
//
// template:
// <quill-editor [modules]="quillModules" [placeholder]="'Enter some text here'" formControlName="name" gccQuillEditor [maxLength]="100"></quill-editor>
